import { FormState } from 'final-form';
import { useCallback, useEffect, useMemo } from 'react';
import { useLocation } from 'react-router-dom';

import { BulkEditPayload, Category, WeeklyTimeSpan } from 'apis/restaurant-api/merchant-admin/menu2/types';
import { useTelemetryViewEvent } from 'common/hooks';
import {
  allDisabledView,
  allItemsInteractionClickCreateItems,
  allItemsInteractionClickDownloadItems,
  allItemsInteractionClickExportMenu,
  allItemsInteractionClickImportItemImages,
  allItemsInteractionClickSelectImages,
  allItemsInteractionClickStartMenuImport,
  allItemsInteractionClickCreateSelectFile,
  allItemsView,
  allOptionsView,
  categoriesView,
  interactionClickAddNewCategory,
  interactionClickAddSubcategories,
  interactionClickCreateItemCopy,
  interactionClickEditCategory,
  interactionClickEditOption,
  interactionClickSaveNewItem,
  interactionClickSaveNewOption,
  itemsNoCategoryView,
  ViewValueType,
  allItemsInteractionClickExportVersions,
  allItemsInteractionClickMenuImportSelectFile,
  allItemsInteractionClickImportMenu,
  allItemsInteractionClickStartUploading,
  allItemsInteractionClickUpdateItems,
  allItemsInteractionClickSubmitItemUpdating,
  allItemsInteractionClickSubmitItemsCreation,
  allItemsInteractionClickUpdateSelectFile,
  interactionClickAddOptions,
  interactionClickChangeCategory,
  interactionClickChangeCommissions,
  interactionClickChangePrice,
  interactionClickChangeStockAvailability,
  interactionClickChangeVat,
  interactionClickCreateWoltSpecials,
  interactionClickEnableOrDisable,
  interactionClickSetAvailability,
  interactionClickSetCourierRestrictions,
  interactionClickCreateSpecial,
  interactionClickDeleteItems,
  interactionClickDeleteCategory,
  interactionClickDeleteSubcategory,
  interactionClickDeleteItem,
  interactionClickDisableItem,
  interactionClickEnableItem,
  interactionClickEnableItems,
  interactionClickDisableItems,
  interactionClickExportItems,
  interactionClickImportItems,
  interactionClickRemoveItemFromCategory,
  interactionClickViewOnWolt,
  interactionClickEditSubcategory,
  interactionClickSaveItemChanges,
  interactionClickEditSharedMenu,
  interactionClickShareMenuWithVenues,
  interactionClickSchedulePublishing,
  interactionClickPublishNow,
  interactionClickCreateSharedMenu,
  interactionClickStopSharingMenu,
  AllItemsViewProperties,
  AllOptionsViewProperties,
  ItemsNoCategoryViewProperties,
  AllDisabledViewProperties,
  CategoriesViewProperties,
} from 'core/telemetry/avo/Avo';
import { FileMatch } from 'modules/bulk-image-upload/types';
import { csvColumnsTranslationMap } from 'modules/menu/csv-items/constants';
import { CSVColumnKey } from 'modules/menu/csv-items/types';
import { ListContentType } from 'modules/shared-menu/pages/SharedMenuEditorPage/hooks/useSharedMenuEditorLists';

type TrackViewProperties =
  | AllItemsViewProperties
  | AllOptionsViewProperties
  | AllDisabledViewProperties
  | CategoriesViewProperties
  | ItemsNoCategoryViewProperties;

const listContentTypeToViewFnMap: Record<ListContentType, (args: TrackViewProperties) => void> = {
  ['all-items']: allItemsView,
  ['all-options']: allOptionsView,
  ['all-disabled']: allDisabledView,
  ['items-by-category']: categoriesView,
  ['items-with-no-category']: itemsNoCategoryView,
  ['items-by-subcategory']: () => ({}), // yet to be implemented in Avo
};

export const useSharedMenuViewTracking = (listType: ListContentType, categoryId?: string) => {
  useTelemetryViewEvent(
    listType,
    {
      triggeredFromSharedMenu: true,
      categoryId,
    },
    (durationMs, context) => listContentTypeToViewFnMap[listType]({ durationMs, ...context }),
  );
};

export const useAllItemsDurationViewTracking = () => {
  useTelemetryViewEvent('allItemsClassicEditorView', { triggeredFromSharedMenu: false }, (durationMs, context) =>
    allItemsView({ durationMs, ...context }),
  );
};

export const useAllOptionsDurationViewTracking = () => {
  useTelemetryViewEvent('allOptionsClassicEditorView', { triggeredFromSharedMenu: false }, (durationMs, context) =>
    allOptionsView({ durationMs, ...context }),
  );
};

export const useItemsWithNoCategoryDurationViewTracking = () => {
  useTelemetryViewEvent(
    'itemsWithNoCategoryClassicEditorView',
    { triggeredFromSharedMenu: false },
    (durationMs, context) => itemsNoCategoryView({ durationMs, ...context }),
  );
};

export const useAllDisabledDurationViewTracking = () => {
  useTelemetryViewEvent('allDisabledClassicEditorView', { triggeredFromSharedMenu: false }, (durationMs, context) =>
    allDisabledView({ durationMs, ...context }),
  );
};

export const useCategoriesDurationViewTracking = (categoryId: string) => {
  useTelemetryViewEvent(
    'categoryClassicEditorView',
    { categoryId, triggeredFromSharedMenu: false },
    (durationMs, context) => categoriesView({ durationMs, ...context }),
  );
};

export const useAddItemTracking = () => {
  const view = useNamedView();
  const triggeredFromSharedMenu = useIsFromSharedMenu();
  return (itemId: string, imageUploaded: boolean, warningSelected: string[], changedFields: string[]) =>
    interactionClickSaveNewItem({
      view,
      itemId,
      imageUploaded,
      triggeredFromSharedMenu,
      changedFields,
      mandatoryWarningsSelected: warningSelected.join(', '),
    });
};

export const useSaveItemChangesTracking = () => {
  const view = useNamedView();
  const triggeredFromSharedMenu = useIsFromSharedMenu();
  return useCallback(
    (itemId: string, imageUploaded: boolean, warningSelected: string[], changedFields: string[]) =>
      interactionClickSaveItemChanges({
        view,
        itemId,
        imageUploaded,
        triggeredFromSharedMenu,
        changedFields,
        mandatoryWarningsSelected: warningSelected.join(', '),
      }),
    [triggeredFromSharedMenu, view],
  );
};

export const useCopyItemTracking = () => {
  const view = useNamedView();
  const triggeredFromSharedMenu = useIsFromSharedMenu();
  return (changedFields: string[]) =>
    interactionClickCreateItemCopy({
      view,
      triggeredFromSharedMenu,
      changedFields,
    });
};

export const useCreateWoltSpecialsTracking = () => {
  const view = useNamedView();
  const triggeredFromSharedMenu = useIsFromSharedMenu();
  return (changedFields: string[]) =>
    interactionClickCreateSpecial({
      view,
      triggeredFromSharedMenu,
      changedFields,
    });
};

// Those hooks are group because they are used at the same place
export const useCreateItemTracking = () => ({
  trackAddItem: useAddItemTracking(),
  trackCopyItem: useCopyItemTracking(),
  trackCreateSpecial: useCreateWoltSpecialsTracking(),
});

export const useCreateOptionsTracking = () => {
  const view = useNamedView();
  const triggeredFromSharedMenu = useIsFromSharedMenu();
  return (optionId: string, changedFields: string[]) =>
    interactionClickSaveNewOption({
      view,
      optionId,
      triggeredFromSharedMenu,
      changedFields,
    });
};

export const useEditOptionsTracking = () => {
  const view = useNamedView();
  const triggeredFromSharedMenu = useIsFromSharedMenu();
  return (optionId: string, changedFields: string[]) =>
    interactionClickEditOption({
      view,
      optionId,
      changedFields,
      triggeredFromSharedMenu,
    });
};

export const useCreateCategoriesTracking = () => {
  const view = useNamedView();
  const triggeredFromSharedMenu = useIsFromSharedMenu();
  return useCallback(
    (categoriesAdded: string[], imageUploaded: boolean) =>
      interactionClickAddNewCategory({
        view,
        imageUploaded,
        triggeredFromSharedMenu,
        categoriesAdded: categoriesAdded.join(', '),
      }),
    [triggeredFromSharedMenu, view],
  );
};

export const useEditCategoriesTracking = () => {
  const view = useNamedView();
  const triggeredFromSharedMenu = useIsFromSharedMenu();
  return useCallback(
    (categoryId: string, changedFields: string[], imageUploaded: boolean) =>
      interactionClickEditCategory({
        view,
        categoryId,
        triggeredFromSharedMenu,
        imageUploaded,
        changedFields,
      }),
    [triggeredFromSharedMenu, view],
  );
};

export const useAddSubcategoriesTracking = () => {
  const view = useNamedView();
  const triggeredFromSharedMenu = useIsFromSharedMenu();
  return useCallback(
    (categoryId: string, subcategoriesAdded: string[]) =>
      interactionClickAddSubcategories({
        view,
        categoryId,
        triggeredFromSharedMenu,
        subcategoriesAdded,
      }),
    [triggeredFromSharedMenu, view],
  );
};

export const useEditSubcategoryTracking = () => {
  const view = useNamedView();
  return useCallback(
    (categoryId: string, changedFields: string[]) =>
      interactionClickEditSubcategory({
        view,
        categoryId,
        changedFields,
      }),
    [view],
  );
};

// This hook just gathers hooks that have a good chance to be used in the same component
export const useMenuActionsTracking = () => ({
  trackCreateItemsClick: () => allItemsInteractionClickCreateItems({ triggeredFromSharedMenu: false }),
  trackImportImages: () => allItemsInteractionClickImportItemImages({ triggeredFromSharedMenu: false }),
  trackExportMenuAsJson: () => allItemsInteractionClickExportMenu({ triggeredFromSharedMenu: false }),
  trackStartImportMenuAsJson: () => allItemsInteractionClickStartMenuImport({ triggeredFromSharedMenu: false }),
  trackStartUpdateItemsAsCsv: () => allItemsInteractionClickUpdateItems({ triggeredFromSharedMenu: false }),
});

export const useEditSharedMenuTracking = () => {
  const view = useNamedView();
  return (sharedMenuId: string) => interactionClickEditSharedMenu({ view, sharedMenuId });
};

export const useCreateSharedMenuTracking = () => {
  const view = useNamedView();
  return (sharedMenuId: string, menuLanguage: string) =>
    interactionClickCreateSharedMenu({
      view,
      sharedMenuId,
      fromMenuLanguage: menuLanguage,
    });
};

export const useStopSharingMenuTracking = (
  selectAllSelected: boolean,
  sharedMenuId: string,
  userSearchQuery: string,
  venueIdSelected: string[],
) => {
  const view = useNamedView();
  return useCallback(
    () =>
      interactionClickStopSharingMenu({
        view,
        sharedMenuId,
        userSearchQuery,
        selectAllSelected,
        venueIdSelected,
      }),
    [view, selectAllSelected, sharedMenuId, userSearchQuery, venueIdSelected],
  );
};

export const useShareMenuWithVenuesTracking = (
  selectAllSelected: boolean,
  sharedMenuId: string,
  userSearchQuery: string,
  venueIdSelected: string[],
) => {
  const view = useNamedView();
  return useCallback(
    () =>
      interactionClickShareMenuWithVenues({
        view,
        selectAllSelected,
        sharedMenuId,
        userSearchQuery,
        venueIdSelected,
      }),
    [view, selectAllSelected, sharedMenuId, userSearchQuery, venueIdSelected],
  );
};

export const usePublishSharedMenuTracking = (sharedMenuId: string) => {
  const view = useNamedView();
  return (dateSelected: Date) =>
    interactionClickSchedulePublishing({ view, sharedMenuId, dateSelected: dateSelected.getTime() });
};

export const useImmediatePublishSharedMenuTracking = (sharedMenuId: string) => {
  const view = useNamedView();
  return () => interactionClickPublishNow({ view, sharedMenuId });
};

// make this tracking available for shared menu or create a new event
export const trackUpdateItemsFromCsv = (status: string, selectedColumns: CSVColumnKey[]) =>
  allItemsInteractionClickSubmitItemUpdating({
    importStatus: status,
    selectedFields: selectedColumns.join(', '),
    triggeredFromSharedMenu: false,
  });

// make this tracking available for shared menu or create a new event
export const trackCreateItemsFromCsv = (status: string, categorySelected: string) =>
  allItemsInteractionClickSubmitItemsCreation({
    importStatus: status,
    categorySelected,
    triggeredFromSharedMenu: false,
  });

// make this tracking available for shared menu or create a new event
export const trackSelectCSVForItemUpdate = (status: string) =>
  allItemsInteractionClickUpdateSelectFile({ importStatus: status, triggeredFromSharedMenu: false });

export const trackSelectCSVForImport = (message: string) =>
  allItemsInteractionClickCreateSelectFile({ importStatus: message, triggeredFromSharedMenu: false });

export const trackSelectJSONForImport = (message: string) =>
  allItemsInteractionClickMenuImportSelectFile({ importStatus: message, triggeredFromSharedMenu: false });

export const useTrackImportImageUpload = (status: 'loading' | 'error' | 'idle' | 'success') => {
  const triggeredFromSharedMenu = useIsFromSharedMenu();
  useEffect(() => {
    if (status === 'success' || status === 'error') {
      const importStatus = status === 'success' ? 'Success' : 'Failure';
      allItemsInteractionClickStartUploading({ importStatus, triggeredFromSharedMenu });
    }
  }, [status, triggeredFromSharedMenu]);
};

export const trackImportJSONMenu = (status: string, keepAvailabilityInfo: boolean, keepPriceInfo: boolean) =>
  allItemsInteractionClickImportMenu({
    importStatus: status,
    triggeredFromSharedMenu: false,
    keepAvailabilityInformation: keepAvailabilityInfo,
    keepPriceInformation: keepPriceInfo,
  });

export const trackExportPastMenu = (message: string, date: Date) =>
  allItemsInteractionClickExportVersions({
    exportStatus: message,
    dateSelected: date.getTime(),
    triggeredFromSharedMenu: false,
  });

export const useTrackImportItemImagesFileSelected = (matchingResults: FileMatch[]) => {
  const triggeredFromSharedMenu = useIsFromSharedMenu();
  useEffect(() => {
    if (matchingResults.length > 0) {
      const notMatched = matchingResults.filter(file => file.status === 'failure');
      const errors = notMatched.map(file => file.file.name).join(', ');
      allItemsInteractionClickSelectImages({
        triggeredFromSharedMenu,
        matchingStatus: notMatched.length === 0 ? 'Success' : `Failed: ${errors}`,
        numberOfImagesSelected: matchingResults.length,
      });
    }
  }, [matchingResults, triggeredFromSharedMenu]);
};

export const trackDownloadItemsAsCsv = (selectedColumns: CSVColumnKey[]) => {
  const existingFields = Object.keys(csvColumnsTranslationMap);
  // We assume here that 'selectedColumns' can only be a subset of 'existingFields'
  const selectAllSelected = selectedColumns.length === existingFields.length;
  const fieldsSelected = selectAllSelected ? '' : selectedColumns.join(', ');
  allItemsInteractionClickDownloadItems({ selectAllSelected, fieldsSelected, triggeredFromSharedMenu: false });
};

export const useAddOptionsTracking = (selectedItems: string[], optionsSelected: string[]) => {
  const view = useNamedView();
  const triggeredFromSharedMenu = useIsFromSharedMenu();
  return () =>
    interactionClickAddOptions({
      view,
      triggeredFromSharedMenu,
      selectedItems,
      optionsSelected,
    });
};

export const useBulkChangeCategoryTracking = (selectedItemIds: string[], categoryId: string) => {
  const view = useNamedView();
  const triggeredFromSharedMenu = useIsFromSharedMenu();
  return () =>
    interactionClickChangeCategory({
      view,
      categoryId,
      triggeredFromSharedMenu,
      selectedItems: selectedItemIds,
    });
};

export const useBulkChangeCommissionTracking = (selectedItemIds: string[]) => {
  const view = useNamedView();
  const triggeredFromSharedMenu = useIsFromSharedMenu();
  return (takeawayComission: string, woltDeliveryCommission: string) =>
    interactionClickChangeCommissions({
      view,
      takeawayComission,
      woltDeliveryCommission,
      triggeredFromSharedMenu,
      selectedItems: selectedItemIds,
    });
};

export const useBulkChangeVatTracking = (selectedItemIds: string[]) => {
  const view = useNamedView();
  const triggeredFromSharedMenu = useIsFromSharedMenu();
  return (vatAmountEntered: number) =>
    interactionClickChangeVat({
      view,
      vatAmountEntered,
      triggeredFromSharedMenu,
      selectedItems: selectedItemIds,
    });
};

type WoltSpecialsEvent = {
  discountAmount: number;
  startDateSelected: number;
  endDateSelected: number;
  firstTimeCustomers: string;
};

export const useBulkCreateWoltSpecialsTracking = (selectedItemIds: string[]) => {
  const view = useNamedView();
  const triggeredFromSharedMenu = useIsFromSharedMenu();
  return (woltSpecialSettings: WoltSpecialsEvent) =>
    interactionClickCreateWoltSpecials({
      view,
      woltSpecialSettings,
      selectedItems: selectedItemIds,
      triggeredFromSharedMenu,
    });
};

export const useBulkDeleteTracking = (selectedItemIds: string[]) => {
  const view = useNamedView();
  const triggeredFromSharedMenu = useIsFromSharedMenu();
  return () => interactionClickDeleteItems({ view, selectedItems: selectedItemIds, triggeredFromSharedMenu });
};

export const useBulkChangeEnableStatusTracking = (selectedItemIds: string[]) => {
  const view = useNamedView();
  const triggeredFromSharedMenu = useIsFromSharedMenu();
  return (enabled: boolean) =>
    interactionClickEnableOrDisable({
      view,
      enableItems: enabled,
      disableItems: !enabled,
      selectedItems: selectedItemIds,
      triggeredFromSharedMenu,
    });
};

export const useBulkChangeDeliveryTracking = (selectedItemIds: string[]) => {
  const view = useNamedView();
  const triggeredFromSharedMenu = useIsFromSharedMenu();
  return (restrictionSelected: string) =>
    interactionClickSetCourierRestrictions({
      view,
      restrictionSelected,
      selectedItems: selectedItemIds,
      triggeredFromSharedMenu,
    });
};

export const useBulkChangeVisibilityAndAvailabilityTracking = (selectedItemIds: string[]) => {
  const view = useNamedView();
  const triggeredFromSharedMenu = useIsFromSharedMenu();
  return (
    selectedAvailability: WeeklyTimeSpan[] | null | undefined,
    selectedVisibility: WeeklyTimeSpan[] | null | undefined,
  ) =>
    interactionClickSetAvailability({
      view,
      selectedVisibility: (selectedVisibility ?? []).join('-'),
      selectedAvailability: (selectedAvailability ?? []).join('-'),
      selectedItems: selectedItemIds,
      triggeredFromSharedMenu,
    });
};

export const useBulkChangeStockAvailabilityTracking = (selectedItemIds: string[]) => {
  const view = useNamedView();
  const triggeredFromSharedMenu = useIsFromSharedMenu();
  return useCallback(
    (markedInStock: boolean) =>
      interactionClickChangeStockAvailability({
        view,
        markedInStock,
        markedOutOfStock: !markedInStock,
        selectedItems: selectedItemIds,
        triggeredFromSharedMenu,
      }),
    [selectedItemIds, triggeredFromSharedMenu, view],
  );
};

export const useBulkChangePriceTracking = (selectedItemIds: string[]) => {
  const view = useNamedView();
  const triggeredFromSharedMenu = useIsFromSharedMenu();
  return (payload: BulkEditPayload) => {
    const priceChangingType = payload.price?.change_type || payload.apply_discount?.discount_type || '';
    const priceChangingAmount = payload.price?.change_amount || payload.apply_discount?.discount_amount || '';
    const priceChangingDirection = !priceChangingAmount.startsWith('-') && payload.price ? 'addition' : 'deduction';
    interactionClickChangePrice({
      view,
      priceChangingType,
      priceChangingAmount,
      priceChangingDirection,
      selectedItems: selectedItemIds,
      triggeredFromSharedMenu,
    });
  };
};

export const useDeleteCategoryTracking = (categoryId: string, type: 'category' | 'subcategory') => {
  const view = useNamedView();
  const triggeredFromSharedMenu = useIsFromSharedMenu();
  return type === 'category'
    ? () => interactionClickDeleteCategory({ view, categoryId, triggeredFromSharedMenu })
    : () => interactionClickDeleteSubcategory({ view, categoryId });
};

export const useItemActionTracking = () => {
  const view = useNamedView();
  const triggeredFromSharedMenu = useIsFromSharedMenu();
  return {
    trackDeleteItem: useCallback(
      (itemId: string) => interactionClickDeleteItem({ view, itemId, triggeredFromSharedMenu }),
      [triggeredFromSharedMenu, view],
    ),
    trackDisableItem: useCallback(
      (itemId: string) => interactionClickDisableItem({ view, itemId, triggeredFromSharedMenu }),
      [triggeredFromSharedMenu, view],
    ),
    trackEnableItem: useCallback(
      (itemId: string) => interactionClickEnableItem({ view, itemId, triggeredFromSharedMenu }),
      [triggeredFromSharedMenu, view],
    ),
    trackRemoveItemFromCategory: useCallback(
      (itemId: string, categoryId: string) =>
        interactionClickRemoveItemFromCategory({ view, itemId, categoryId, triggeredFromSharedMenu }),
      [triggeredFromSharedMenu, view],
    ),
  };
};

export const useEnableItemsTracking = (categories: Pick<Category, 'id'>[]) => {
  const view = useNamedView();
  const triggeredFromSharedMenu = useIsFromSharedMenu();
  const categoryId = categories.map(category => category.id).join(', ');
  return (enabled: boolean, selectedItems: string[]) =>
    (enabled ? interactionClickEnableItems : interactionClickDisableItems)({
      view,
      categoryId,
      selectedItems,
      triggeredFromSharedMenu,
    });
};

export const useExportItemsTracking = (categoryId: string) => {
  const view = useNamedView();
  const triggeredFromSharedMenu = useIsFromSharedMenu();
  return (exportStatus: string, selectedItems: string[]) =>
    interactionClickExportItems({ view, categoryId, exportStatus, selectedItems, triggeredFromSharedMenu });
};

export const useImportItemsTracking = (categoryId: string) => {
  const view = useNamedView();
  const triggeredFromSharedMenu = useIsFromSharedMenu();
  return (errors: { error: string | undefined }[]) => {
    const importStatus = errors.length ? `Failure: ${errors.map(err => err.error).join(', ')}` : 'Success';
    interactionClickImportItems({ view, categoryId, importStatus, triggeredFromSharedMenu });
  };
};

export const useRemoveFromCategoryTracking = (itemId: string, categoryId: string) => {
  const view = useNamedView();
  const triggeredFromSharedMenu = useIsFromSharedMenu();
  return () => interactionClickRemoveItemFromCategory({ view, itemId, categoryId, triggeredFromSharedMenu });
};

export const useViewOnWoltTracking = () => {
  const view = useNamedView();
  const triggeredFromSharedMenu = useIsFromSharedMenu();
  return () => interactionClickViewOnWolt({ view, triggeredFromSharedMenu });
};

export const useNamedView = (): ViewValueType => {
  const location = useLocation();
  return useMemo(() => getViewFromLocation(location.pathname), [location.pathname]);
};

export const useIsFromSharedMenu = () => {
  const location = useLocation();

  const isSharedMenu = !!location.pathname.match(/\/shared-menu/)?.length;
  const isVenueWithSharedMenuInName = !!location.pathname.match(/\/venue\/shared-menu/)?.length;

  return isSharedMenu && !isVenueWithSharedMenuInName;
};

const routePatterns: Readonly<[ViewValueType, RegExp][]> = [
  ['categories', /menu-editor\/(\d|\w)+\/subcategories\/(\d|\w)+$/],
  ['all_items', /menu-editor$/],
  ['all_disabled', /menu-editor\/all-disabled$/],
  ['items_no_category', /menu-editor\/no-category$/],
  ['all_options', /menu-editor\/options$/],
  ['categories', /menu-editor\/(\d|\w)+$/],
  ['categories', /menu-editor\/add-categories/],
];

const getViewFromLocation = (location: string): ViewValueType =>
  (routePatterns.find(([_, pattern]) => pattern.test(location)) || ['menu_editor'])[0];

export const getModifiedFieldsForTracking = <T>(formState: FormState<T, Partial<T>>): string[] =>
  !formState.pristine
    ? Object.entries(formState?.modified || {})
        .filter(([_, modified]) => modified)
        .map(([field, _]) => field)
    : [];
